import {
  Documentation_PageMetadataKey,
  type Documentation_RelatedPagesFilter,
} from '@tyro/api';
import { useMemo } from 'react';
import {
  type ReturnTypeFromUseDocumentationRelatedPages,
  useDocumentationRelatedPages,
} from '../api/pages';
import {
  getPageMetaValues,
  type PageMetadataRow,
} from '../utils/page-metadata';

export function relatedDocKey(
  page: Pick<
    ReturnTypeFromUseDocumentationRelatedPages,
    'spaceSlug' | 'pageSlug'
  >,
): string {
  return `${page.spaceSlug}/${page.pageSlug}`;
}

export function buildRelatedDocsIndex(
  linkablePages: ReturnTypeFromUseDocumentationRelatedPages[] | undefined,
): Map<string, ReturnTypeFromUseDocumentationRelatedPages> {
  return new Map(
    (linkablePages ?? []).map((entry) => [relatedDocKey(entry), entry]),
  );
}

export function resolveRelatedDocs(
  metadata: PageMetadataRow[] | undefined,
  linkablePages: ReturnTypeFromUseDocumentationRelatedPages[] | undefined,
): ReturnTypeFromUseDocumentationRelatedPages[] {
  if (!metadata || !linkablePages) return [];
  const index = buildRelatedDocsIndex(linkablePages);
  return getPageMetaValues(metadata, Documentation_PageMetadataKey.RelatedDocs)
    .map((value) => index.get(value))
    .filter(
      (entry): entry is ReturnTypeFromUseDocumentationRelatedPages =>
        entry !== undefined,
    );
}

export function useResolvedRelatedDocs(
  metadata: PageMetadataRow[] | undefined,
  filter: Documentation_RelatedPagesFilter = {},
): ReturnTypeFromUseDocumentationRelatedPages[] {
  const { data: linkablePages } = useDocumentationRelatedPages(filter);
  return useMemo(
    () => resolveRelatedDocs(metadata, linkablePages),
    [metadata, linkablePages],
  );
}
